import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {ILocationService, IPromise, IRootScopeService} from "angular";
import {StateService} from "@uirouter/angularjs";
import {Kontekst} from "felles/tjenester/Kontekst";
import {l} from "types/typescriptdefinisjoner_lederplan";
import _ from "lodash";
import {gettextCatalog} from "angular-gettext";
import {OppgaveRessurs} from "felles/tjenester/ressurser/OppgaveRessurs";
import {IntroJsTjeneste} from "felles/tjenester/IntroJs";
import {InnlastingsindikatorKontroller} from "web/moduler/basis/direktiver/innlastingsindikator";
import {OppgaverFilterKontroller} from "./komponenter/oppgaverFilter/oppgaverFilter";
import {OppgaveTjenesteWeb} from "./OppgaveTjenesteWeb";
import Oppgavesorteringskolonne = l.Oppgavesorteringskolonne;
import Sortering = l.Sortering;
import OppgavePagineringSpesifikasjon = l.OppgavePagineringSpesifikasjon;
import {fraJson} from "felles/stoettefunk/json";
import {erIkkeNill} from "felles/stoettefunk/nillsjekk";

const css = require('./oppgaver.scss');

export class OppgaverKontroller {

    static regNavn = "wm.oppgave.OppgaverKontroller";
    static gyldigeVisninger = ['kanban', 'liste'];

    static forInjisering() {
        return [
            regNavn.ng.$rootScope,
            regNavn.ng.$location,
            regNavn.ng.ui.$state,
            OppgaveRessurs.regNavn,
            OppgaveTjenesteWeb.regNavn,
            Kontekst.regNavn,
            IntroJsTjeneste.regNavn,
            regNavn.gettext.gettextCatalog,
            OppgaverKontroller
        ]
    }

    constructor(private $rootScope: IRootScopeService,
                private $location: ILocationService,
                private $state: StateService,
                private oppgaveRes: OppgaveRessurs,
                private oppgaveTjenesteWeb: OppgaveTjenesteWeb,
                private kontekst: Kontekst,
                private introJs: IntroJsTjeneste,
                private gettextCatalog: gettextCatalog) {

        this.visning = this.lesVisningFraUrl();
        this.aktivPaginering = this.lesPagingeringFraUrlEllerGiStandard();
        this.initIntroJs();
    }

    oppgaver: l.OppgaveModell[] = null;

    // brukes av listevisning sin pagineringsboks
    metadata: Readonly<PagineringMetadata>;

    private visning: 'kanban' | 'liste';

    private readonly standardPaginering: Readonly<l.OppgavePagineringSpesifikasjon> = {maksAntall: 100, offsett: 0};
    private readonly standardPagineringKanban: Readonly<l.OppgavePagineringSpesifikasjon> = {maksAntall: 2500, offsett: 0};
    private readonly standardSortering: Readonly<l.OppgaveSorteringsspesifikasjon> = {kolonne: Oppgavesorteringskolonne.frist, retning: Sortering.stigende}

    private oppgaverFilterKontroller: OppgaverFilterKontroller;
    private sisteSokeSpesifikasjon: l.FinnOppgaverSpesifikasjon;


    // brukes av listevisning
    private aktivSortering:l.OppgaveSorteringsspesifikasjon = this.standardSortering;
    private aktivPaginering:l.OppgavePagineringSpesifikasjon = this.standardPaginering;

    registrerFilterKontroller(kontroller: OppgaverFilterKontroller) {
        this.oppgaverFilterKontroller = kontroller;
    }

    visResultatSomListe() {
        this.settVisning("liste");
    }

    visResultatSomKanban() {
        this.settVisning('kanban');
    }

    erVisResultatSomListe() {
        return this.visning == 'liste';
    }

    erVisResultatSomKanban() {
        return this.visning == 'kanban';
    }

    paginer(spesifikasjon: l.Pagineringsoffsett) {
        const nySpesifikasjon = {...this.sisteSokeSpesifikasjon, paginering: {maksAntall: this.aktivPaginering.maksAntall, offsett: spesifikasjon.offsett}};
        this.finnOppgaver(nySpesifikasjon)
    }
    
    sorter(spesifikasjon :l.OppgaveSorteringsspesifikasjon) {
        const nySpesifikasjon = {...this.sisteSokeSpesifikasjon, sortering: spesifikasjon}
        this.finnOppgaver(nySpesifikasjon);
    }

    oppgaveModellOppdatert(spesifikasjon) {
        const idx = this.oppgaver.findIndex(o => o.id === spesifikasjon.oppgave.id);
        this.oppgaver[idx] = spesifikasjon.oppgave;
    }

    oppdaterOppgaveStatus(spesifikasjon: {id: string, status: l.Oppgavestatus}) {
        this.oppgaveTjenesteWeb.oppdaterStatus(spesifikasjon).then((oppgave) => {
            let idx = this.oppgaver.findIndex(o => o.id === oppgave.id);
            // TODO: #1150 - Gjøre serverkall eller erstatte in-situ?
            this.finnOppgaver(this.sisteSokeSpesifikasjon);
            this.oppgaver[idx] = oppgave;
        });
    }

    finnOppgaver(spesifikasjon: l.FinnOppgaverSpesifikasjon): IPromise<l.OppgaveModell[]> {
        if (!spesifikasjon.sortering) spesifikasjon.sortering = this.aktivSortering;
        if (!spesifikasjon.paginering) spesifikasjon.paginering = this.erVisResultatSomKanban() ? this.standardPagineringKanban : this.aktivPaginering;

        InnlastingsindikatorKontroller.visIndikator(this.$rootScope);
        return this.oppgaveRes.finnOppgaverPaginert(spesifikasjon).$promise
          .then(resultat => {
              this.oppgaver = resultat.oppgaver;

              this.metadata = {
                  antallOppgaverIPaginering: resultat.oppgaver.length,
                  maksAntall: spesifikasjon.paginering.maksAntall,
                  offsett: spesifikasjon.paginering.offsett,
                  totaltAntallOppgaver: resultat.antallOppgaver
              };

              this.sisteSokeSpesifikasjon = spesifikasjon;

              if (this.erVisResultatSomListe()) {
                  // Slik at kanban-visning ikke overstyrer paginering og sortering i listevisning
                  this.aktivSortering = spesifikasjon.sortering;
                  this.aktivPaginering = spesifikasjon.paginering;
                  this.skrivPagineringTilUrl();
              }

              if (this.erVisResultatSomKanban() && this.oppgaver.length > 0)
                // Skjul skjemaet så vi ser hele tavla.
                  this.oppgaverFilterKontroller.skjulFilterSkjema();
              return this.oppgaver;
          })
          .finally(() => {
              InnlastingsindikatorKontroller.skjuleIndikator(this.$rootScope);
              this.initIntroJs();
          });
    }

    //hjelpemetoder

    private settVisning(visning: 'kanban' | 'liste') {
        // Endring av listevisning til kanban fører til at vi vil laste oppgaver på nytt,
        // siden kanbanvisningen må laste flere oppgaver upaginert
        if(!_.isNil(this.sisteSokeSpesifikasjon)) {
            this.oppgaver = [];
            // Resett paginering ved bytte av visning
            if(this.sisteSokeSpesifikasjon) {
                this.sisteSokeSpesifikasjon.paginering = visning === 'kanban' ? this.standardPagineringKanban : this.aktivPaginering
            }

            this.finnOppgaver(this.sisteSokeSpesifikasjon)
        }

        this.visning = visning;
        this.skrivVisningTilUrl();
    }

    private lesVisningFraUrl() {
        let visning = this.$state.params["v"];
        if (!_(OppgaverKontroller.gyldigeVisninger).includes(visning))
            visning = this.kontekst.ikkePersistenteBrukervalg().oppgaveVisning;
        if (!_(OppgaverKontroller.gyldigeVisninger).includes(visning))
            visning = 'liste';
        return visning;
    }

    private skrivVisningTilUrl() {
        this.kontekst.oppdaterIkkePersistenteBrukervalg({oppgaveVisning: this.visning});
        this.$location.search("v", this.visning);
    }

    private skrivPagineringTilUrl() {
        this.kontekst.oppdaterIkkePersistenteBrukervalg({oppgavePaginering: this.aktivPaginering});
        this.$location.search("p", angular.toJson(this.aktivPaginering));
    }

    private lesPagingeringFraUrlEllerGiStandard() : OppgavePagineringSpesifikasjon {
        let paginering: l.OppgavePagineringSpesifikasjon = fraJson(this.$state.params["p"]);

        if(!_.isNil(paginering) && paginering.maksAntall > this.standardPaginering.maksAntall) {
            return this.standardPaginering;
        }

        return paginering || this.standardPaginering;
    }


    private initIntroJs() {

        const intro = this.introJs.nyIntroduksjon('oppgaver')
            .leggTilSteg(
                  1,
                  {
                    title: this.gettextCatalog.getString('Filterpanel'),
                    intro: this.gettextCatalog.getString('Her velger du kriterier for hvilke oppgaver du vil se i listen eller på tavlen.'),
                    element: '[data-tips-id="oppgave-filter"]',
                    position: 'bottom'
                }
            )
            .leggTilSteg(
                  2,
                  {
                    title: this.gettextCatalog.getString('Endre visning'),
                    intro: this.gettextCatalog.getString('Velg mellom liste- eller tavle-visning.'),
                    element: '[data-tips-id="oppgaver-visning"]',
                    position: 'bottom'
                }
            )
            .leggTilSteg(
                    3,
                  {
                        title: this.gettextCatalog.getString('Sortering av oppgaver'),
                        intro: this.gettextCatalog.getString('Du kan sortere oppgavene på frist, tittel, status, og ansvarlig.'),
                        element: 'wo-oppgaveliste [data-tips-id="oppgaveliste-tittelrad"]',
                        position: 'top'
                    },
                    () => this.erVisResultatSomListe() && this.oppgaver?.length > 0
            )
            .leggTilSteg(
                  4,
                    {
                        title: this.gettextCatalog.getString('Status på oppgaven'),
                        intro: this.gettextCatalog.getString('Du kan enkelt endre status på oppgavene for å holde prosessen oppdatert.'),
                        element: 'wo-oppgave [data-tips-id="oppgave-status-velger"]'
                    },
                    () => this.erVisResultatSomListe() && this.oppgaver?.length > 0
            )
            .leggTilSteg(
                5,
                {
                    title: this.gettextCatalog.getString('Oppgavemeny'),
                    intro: this.gettextCatalog.getString('I oppgavemenyen kan du redigere oppgaven din, og markere den som favoritt.'),
                    element: 'wo-oppgave [data-tips-id="komponent-meny-trigger"]'
                },
                () => {
                    return this.oppgaver?.length > 0 &&       // Vi har oppgaver
                        this.erVisResultatSomListe() &&      // i liste-visning
                        (
                            // noen av oppgavene er tildelt aktiv bruker
                            this.oppgaver.some( (oppgave) => erIkkeNill(oppgave.ansvarlig) && oppgave.ansvarlig.brukerId == this.kontekst.autentisertBrukersId()) ||
                            // eller aktiv bruker har skriverettigheter
                            this.kontekst.inneharTilgangsrolleINoenGruppe(l.TilgangsrolleMedlem.skrive)
                        )
                }
            )
            .leggTilSteg(
                6,
                {
                    title: this.gettextCatalog.getString('Dra-og-slipp'),
                    intro: this.gettextCatalog.getString('Bruk dra-og-slipp for å flytte oppgaver mellom kolonner. Oppgavestatusen oppdateres automatisk!'),
                    element: '[data-tips-id="oppgaver-kolonne-1"]'
                },
                () => this.erVisResultatSomKanban()
            )
            .leggTilSteg(
                7,
                {
                    title: this.gettextCatalog.getString('Se oppgavedetaljer'),
                    intro: this.gettextCatalog.getString('Trykk på oppgavetittelen for å se detaljer for oppgaven, og redigere den.'),
                    element: '.wok-oppgaver_kanban [data-tips-id="oppgaver-kanban-oppgave"]:nth-of-type(1)'
                },
                () => this.erVisResultatSomKanban()
            )
        ;

        this.introJs.visIntroForsinket(intro, 1000);
    }
}

export interface PagineringMetadata {
    totaltAntallOppgaver:number;
    antallOppgaverIPaginering:number;
    maksAntall:number;
    offsett:number;
}

